/* ==========================================================================
   JavaScript Animation Engine - Bypasses Windows Animation Settings
   Asian American Forward Website
   ========================================================================== */

// Animation registry to track active animations
const activeAnimations = new Map();
let animationId = 0;

/* ==========================================================================
   Core Animation Engine
   ========================================================================== */

/**
 * Creates a JavaScript animation that bypasses Windows animation settings
 * @param {HTMLElement} element - The element to animate
 * @param {Object} properties - Properties to animate (e.g., { opacity: 1, translateX: 0 })
 * @param {Object} options - Animation options
 * @returns {Promise} Promise that resolves when animation completes
 */
export function animate(element, properties, options = {}) {
  const {
    duration = 300,
    easing = 'ease-out',
    delay = 0,
    fill = 'forwards'
  } = options;

  return new Promise((resolve) => {
    // Cancel any existing animation on this element
    cancelElementAnimations(element);

    // Get starting values
    const startValues = {};
    const endValues = {};
    const computedStyle = window.getComputedStyle(element);

    // Parse properties and get initial values
    Object.keys(properties).forEach(prop => {
      const currentValue = getCurrentValue(element, prop, computedStyle);
      startValues[prop] = currentValue;
      endValues[prop] = properties[prop];
    });

    // Store initial styles to restore later if needed
    if (!element._originalStyles) {
      element._originalStyles = {};
      Object.keys(properties).forEach(prop => {
        element._originalStyles[prop] = getCurrentValue(element, prop, computedStyle);
      });
    }

    // Animation state
    const animation = {
      id: ++animationId,
      element,
      startTime: null,
      duration,
      delay,
      easing: getEasingFunction(easing),
      startValues,
      endValues,
      onComplete: resolve,
      fill
    };

    // Start animation after delay
    if (delay > 0) {
      setTimeout(() => startAnimation(animation), delay);
    } else {
      startAnimation(animation);
    }

    // Store animation reference
    activeAnimations.set(animation.id, animation);
  });
}

/**
 * Animates an element's entrance (fade in + slide)
 */
export function animateEntrance(element, direction = 'up', options = {}) {
  const distance = options.distance || 30;
  const duration = options.duration || 600;
  const delay = options.delay || 0;

  // Set initial state
  const initialTransform = getTransformForDirection(direction, distance);
  element.style.opacity = '0';
  element.style.transform = initialTransform;

  return animate(element, {
    opacity: 1,
    translateX: 0,
    translateY: 0
  }, {
    duration,
    delay,
    easing: 'ease-out'
  });
}

/**
 * Animates an element's exit (fade out + slide)
 */
export function animateExit(element, direction = 'up', options = {}) {
  const distance = options.distance || 30;
  const duration = options.duration || 300;

  const finalTransform = getTransformForDirection(direction, distance);
  const transforms = parseTransform(finalTransform);

  return animate(element, {
    opacity: 0,
    ...transforms
  }, {
    duration,
    easing: 'ease-in'
  });
}

/**
 * Animates a hover effect
 */
export function animateHover(element, hoverProperties, options = {}) {
  const duration = options.duration || 200;
  
  return animate(element, hoverProperties, {
    duration,
    easing: 'ease-out'
  });
}

/**
 * Resets hover animation
 */
export function resetHover(element, options = {}) {
  const duration = options.duration || 200;
  
  // Reset to original values
  if (element._originalStyles) {
    return animate(element, element._originalStyles, {
      duration,
      easing: 'ease-out'
    });
  }
  
  return Promise.resolve();
}

/* ==========================================================================
   Special Effect Animations
   ========================================================================== */

/**
 * Pulse animation
 */
export function animatePulse(element, options = {}) {
  const { duration = 2000, minOpacity = 0.7, maxOpacity = 1 } = options;
  
  const pulseAnimation = () => {
    animate(element, { opacity: minOpacity }, { duration: duration / 2, easing: 'ease-in-out' })
      .then(() => animate(element, { opacity: maxOpacity }, { duration: duration / 2, easing: 'ease-in-out' }))
      .then(() => {
        if (element._shouldPulse) {
          pulseAnimation();
        }
      });
  };
  
  element._shouldPulse = true;
  pulseAnimation();
  
  return {
    stop: () => {
      element._shouldPulse = false;
    }
  };
}

/**
 * Rainbow border animation (for the donate button)
 */
export function animateRainbowBorder(element, options = {}) {
  const { duration = 3000 } = options;
  
  const colors = [
    '#084874', // Blue
    '#3c4f82', // Blue to purple transition
    '#782b8b', // Purple
    '#8b2a5a', // Purple to red transition
    '#8f2226', // Red
    '#5b4650'  // Red to blue transition
  ];
  
  let colorIndex = 0;
  
  const rainbowAnimation = () => {
    const color = colors[colorIndex];
    colorIndex = (colorIndex + 1) % colors.length;
    
    animate(element, { borderColor: color }, { 
      duration: duration / colors.length, 
      easing: 'ease-in-out'
    }).then(() => {
      if (element._shouldRainbow) {
        rainbowAnimation();
      }
    });
  };
  
  element._shouldRainbow = true;
  rainbowAnimation();
  
  return {
    stop: () => {
      element._shouldRainbow = false;
    }
  };
}

/**
 * Spin animation
 */
export function animateSpin(element, options = {}) {
  const { duration = 1000, continuous = true } = options;
  
  const spinAnimation = () => {
    animate(element, { rotate: 360 }, { duration, easing: 'linear' })
      .then(() => {
        if (continuous && element._shouldSpin) {
          // Reset rotation and continue
          element.style.transform = element.style.transform.replace(/rotate\([^)]*\)/, 'rotate(0deg)');
          spinAnimation();
        }
      });
  };
  
  element._shouldSpin = true;
  spinAnimation();
  
  return {
    stop: () => {
      element._shouldSpin = false;
    }
  };
}

/* ==========================================================================
   Helper Functions
   ========================================================================== */

function startAnimation(animation) {
  const tick = (currentTime) => {
    if (animation.startTime === null) {
      animation.startTime = currentTime;
    }
    
    const elapsed = currentTime - animation.startTime;
    const progress = Math.min(elapsed / animation.duration, 1);
    const easedProgress = animation.easing(progress);
    
    // Apply interpolated values
    Object.keys(animation.endValues).forEach(prop => {
      const startValue = animation.startValues[prop];
      const endValue = animation.endValues[prop];
      const currentValue = interpolateValue(startValue, endValue, easedProgress);
      
      applyProperty(animation.element, prop, currentValue);
    });
    
    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      // Animation complete
      activeAnimations.delete(animation.id);
      animation.onComplete();
    }
  };
  
  requestAnimationFrame(tick);
}

function getCurrentValue(element, property, computedStyle) {
  switch (property) {
    case 'opacity':
      return parseFloat(computedStyle.opacity) || 1;
    case 'translateX':
    case 'translateY':
    case 'translateZ':
    case 'rotate':
    case 'rotateX':
    case 'rotateY':
    case 'scale':
      return parseTransformValue(element, property);
    case 'borderColor':
      return computedStyle.borderColor || '#000000';
    default:
      return parseFloat(computedStyle[property]) || 0;
  }
}

function parseTransformValue(element, property) {
  const transform = element.style.transform || '';
  const regex = new RegExp(`${property}\\(([^)]+)\\)`);
  const match = transform.match(regex);
  
  if (match) {
    const value = match[1];
    return parseFloat(value) || 0;
  }
  
  return property === 'scale' ? 1 : 0;
}

function applyProperty(element, property, value) {
  switch (property) {
    case 'opacity':
      element.style.opacity = value;
      break;
    case 'translateX':
    case 'translateY':
    case 'translateZ':
    case 'rotate':
    case 'rotateX':
    case 'rotateY':
    case 'scale':
      applyTransform(element, property, value);
      break;
    case 'borderColor':
      element.style.borderColor = value;
      break;
    default:
      element.style[property] = typeof value === 'number' ? `${value}px` : value;
  }
}

function applyTransform(element, property, value) {
  const transforms = parseAllTransforms(element.style.transform);
  
  // Update the specific transform
  if (property === 'translateX' || property === 'translateY' || property === 'translateZ') {
    transforms[property] = `${value}px`;
  } else if (property === 'rotate' || property === 'rotateX' || property === 'rotateY') {
    transforms[property] = `${value}deg`;
  } else if (property === 'scale') {
    transforms[property] = value;
  }
  
  // Rebuild transform string
  const transformString = buildTransformString(transforms);
  element.style.transform = transformString;
}

function parseAllTransforms(transformString) {
  const transforms = {
    translateX: '0px',
    translateY: '0px',
    translateZ: '0px',
    rotate: '0deg',
    rotateX: '0deg',
    rotateY: '0deg',
    scale: 1
  };
  
  if (!transformString) return transforms;
  
  // Extract each transform function
  const functions = transformString.match(/\w+\([^)]+\)/g) || [];
  
  functions.forEach(func => {
    const [name, value] = func.split('(');
    const cleanValue = value.replace(')', '');
    
    if (transforms.hasOwnProperty(name)) {
      transforms[name] = cleanValue;
    }
  });
  
  return transforms;
}

function buildTransformString(transforms) {
  const parts = [];
  
  if (transforms.translateX !== '0px') parts.push(`translateX(${transforms.translateX})`);
  if (transforms.translateY !== '0px') parts.push(`translateY(${transforms.translateY})`);
  if (transforms.translateZ !== '0px') parts.push(`translateZ(${transforms.translateZ})`);
  if (transforms.rotate !== '0deg') parts.push(`rotate(${transforms.rotate})`);
  if (transforms.rotateX !== '0deg') parts.push(`rotateX(${transforms.rotateX})`);
  if (transforms.rotateY !== '0deg') parts.push(`rotateY(${transforms.rotateY})`);
  if (transforms.scale !== 1) parts.push(`scale(${transforms.scale})`);
  
  return parts.join(' ');
}

function interpolateValue(start, end, progress) {
  if (typeof start === 'number' && typeof end === 'number') {
    return start + (end - start) * progress;
  }
  
  // Handle color interpolation
  if (typeof start === 'string' && typeof end === 'string' && start.startsWith('#')) {
    return interpolateColor(start, end, progress);
  }
  
  return progress < 0.5 ? start : end;
}

function interpolateColor(startColor, endColor, progress) {
  const start = hexToRgb(startColor);
  const end = hexToRgb(endColor);
  
  const r = Math.round(start.r + (end.r - start.r) * progress);
  const g = Math.round(start.g + (end.g - start.g) * progress);
  const b = Math.round(start.b + (end.b - start.b) * progress);
  
  return rgbToHex(r, g, b);
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function getTransformForDirection(direction, distance) {
  switch (direction) {
    case 'up':
      return `translateY(${distance}px)`;
    case 'down':
      return `translateY(-${distance}px)`;
    case 'left':
      return `translateX(${distance}px)`;
    case 'right':
      return `translateX(-${distance}px)`;
    default:
      return `translateY(${distance}px)`;
  }
}

function parseTransform(transformString) {
  const transforms = {};
  const translateXMatch = transformString.match(/translateX\(([^)]+)\)/);
  const translateYMatch = transformString.match(/translateY\(([^)]+)\)/);
  
  if (translateXMatch) {
    transforms.translateX = parseFloat(translateXMatch[1]);
  }
  if (translateYMatch) {
    transforms.translateY = parseFloat(translateYMatch[1]);
  }
  
  return transforms;
}

function getEasingFunction(easing) {
  switch (easing) {
    case 'linear':
      return t => t;
    case 'ease-in':
      return t => t * t;
    case 'ease-out':
      return t => t * (2 - t);
    case 'ease-in-out':
      return t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    case 'cubic-bezier':
      // Simplified cubic-bezier approximation
      return t => t * t * (3 - 2 * t);
    default:
      return t => t * (2 - t); // ease-out default
  }
}

function cancelElementAnimations(element) {
  activeAnimations.forEach((animation, id) => {
    if (animation.element === element) {
      activeAnimations.delete(id);
    }
  });
}

/* ==========================================================================
   Public API
   ========================================================================== */

export function cancelAnimation(element) {
  cancelElementAnimations(element);
}

export function cancelAllAnimations() {
  activeAnimations.clear();
}

export function getActiveAnimationCount() {
  return activeAnimations.size;
}

// Check if Windows animations are disabled
export function areWindowsAnimationsDisabled() {
  // Test if CSS animations are working
  const testElement = document.createElement('div');
  testElement.style.position = 'absolute';
  testElement.style.left = '-9999px';
  testElement.style.animation = 'test-animation 0.01s';
  testElement.style.animationName = 'test-keyframe';
  
  document.body.appendChild(testElement);
  
  // Add test keyframe
  const style = document.createElement('style');
  style.textContent = '@keyframes test-keyframe { from { opacity: 0; } to { opacity: 1; } }';
  document.head.appendChild(style);
  
  const computedStyle = window.getComputedStyle(testElement);
  const isAnimating = computedStyle.animationName === 'test-keyframe';
  
  document.body.removeChild(testElement);
  document.head.removeChild(style);
  
  return !isAnimating;
}

console.log('Creating JavaScript Animation Engine'); 